import React, { useState } from "react";
import {
  Settings,
  History,
  ChevronLeft,
  BarChart2,
  Plus,
  Bell,
  AlertCircle,
  Search,
} from "lucide-react";
import EmailMonitoringSettings from "./EmailMonitoringSettings";
import EmailMonitoringHistory from "./EmailMonitoringHistory";
import DetailedAnalytics from "./DetailedAnalytics";
import AddMonitoringModal from "./AddMonitoringModal";
import DeleteMonitoringModal from "./DeleteMonitoringModal";
import { useLoader } from "../../context/LoaderContext";
import type {
  EmailMonitoring,
  CreateMonitoringRequest,
} from "../../services/EmailHygieneService";
import { emailHygieneService } from "../../services/EmailHygieneService";
import { useToastContext } from "../../context/ToastContext";

interface EmailMonitoringDashboardProps {
  onClose: () => void;
  monitoringList: EmailMonitoring[];
  onMonitoringUpdate: () => void;
}

type TabType = "settings" | "history" | "analytics";

const EmailMonitoringDashboard: React.FC<EmailMonitoringDashboardProps> = ({
  onClose,
  monitoringList,
  onMonitoringUpdate,
}) => {
  const [selectedMonitoring, setSelectedMonitoring] =
    useState<EmailMonitoring | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("settings");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [monitoringToDelete, setMonitoringToDelete] =
    useState<EmailMonitoring | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { showLoader, hideLoader } = useLoader();
  const { showToast } = useToastContext();

  const handleCreateMonitoring = async (data: CreateMonitoringRequest) => {
    try {
      showLoader();
      const response = await emailHygieneService.createMonitoring(data);
      showToast(
        "success",
        response.message || "Monitoring configuration created successfully"
      );
      onMonitoringUpdate();
      setSelectedMonitoring(null);
      setShowAddModal(false);
    } catch (error: any) {
      showToast(
        "error",
        error.message || "Failed to create monitoring configuration"
      );
      throw error;
    } finally {
      hideLoader();
    }
  };

  const handleDeleteClick = (
    e: React.MouseEvent,
    monitoring: EmailMonitoring
  ) => {
    console.log("Monitoring received in dashboard:", monitoring);
    e.stopPropagation();
    setMonitoringToDelete(monitoring);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!monitoringToDelete?.id) {
      showToast("error", "Invalid monitoring ID");
      return;
    }

    try {
      showLoader();
      const response = await emailHygieneService.deleteMonitoring(
        monitoringToDelete.id
      );
      showToast(
        "success",
        response.message || "Monitoring configuration deleted"
      );
      onMonitoringUpdate();
      setSelectedMonitoring(null);
      setShowDeleteModal(false);
      setMonitoringToDelete(null);
    } catch (error: any) {
      showToast("error", error.message || "Failed to delete monitoring");
    } finally {
      hideLoader();
    }
  };

  const filteredMonitors = monitoringList.filter((monitoring) =>
    monitoring.parameterEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background-default dark:bg-background-dark">
      <div className="bg-background-paper dark:bg-background-paperDark">
        <div className="max-w-7xl mx-auto">
          <div className="px-8 h-16 flex items-center justify-between border-b border-divider dark:border-divider-dark">
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="p-2 rounded-lg text-text-secondary hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold">Email Monitoring</h1>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 text-text-secondary absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search monitors..."
                  className="w-64 pl-9 pr-4 py-2 bg-background-panel dark:bg-background-panelDark rounded-lg text-sm border-0 focus:ring-1 focus:ring-yellow-DEFAULT placeholder:text-text-secondary"
                />
              </div>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-3 py-2 bg-yellow-DEFAULT hover:bg-yellow-DEFAULT/90 text-black dark:text-white rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>New Monitor</span>
              </button>
            </div>
          </div>

          <div className="px-8 py-4 flex items-center justify-between border-b border-divider dark:border-divider-dark">
            <div className="flex items-center gap-6">
              {[
                { id: "settings", icon: Settings, label: "Settings" },
                { id: "analytics", icon: BarChart2, label: "Analytics" },
                { id: "history", icon: History, label: "History" },
              ].map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id as TabType)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === id
                      ? "bg-yellow-DEFAULT/10 text-yellow-DEFAULT"
                      : "text-text-secondary hover:bg-background-panel dark:hover:bg-background-panelDark hover:text-text-primary"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </button>
              ))}
            </div>

            {selectedMonitoring && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-background-panel dark:bg-background-panelDark text-sm">
                <Bell className="w-4 h-4 text-yellow-DEFAULT" />
                <span className="font-medium">
                  {selectedMonitoring.parameterEmail}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs ${
                    selectedMonitoring.isActive
                      ? "bg-green-dark/10 text-green-dark"
                      : "bg-red-dark/10 text-red-dark"
                  }`}
                >
                  {selectedMonitoring.isActive ? "Active" : "Inactive"}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8">
        <div className="py-6 grid grid-cols-3 gap-4">
          {filteredMonitors.map((monitoring) => (
            <button
              key={monitoring.id}
              onClick={() => setSelectedMonitoring(monitoring)}
              className={`flex items-start gap-3 p-4 rounded-xl text-left transition-all ${
                selectedMonitoring?.id === monitoring.id
                  ? "bg-yellow-DEFAULT/5 border-2 border-yellow-DEFAULT"
                  : "bg-background-paper dark:bg-background-paperDark border-2 border-transparent hover:border-divider dark:hover:border-divider-dark"
              }`}
            >
              <Bell
                className={`w-5 h-5 mt-0.5 ${
                  selectedMonitoring?.id === monitoring.id
                    ? "text-yellow-DEFAULT"
                    : "text-text-secondary"
                }`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h3 className="font-medium truncate">
                    {monitoring.parameterEmail}
                  </h3>
                  <span
                    className={`shrink-0 px-2 py-0.5 rounded-full text-xs ${
                      monitoring.isActive
                        ? "bg-green-dark/10 text-green-dark"
                        : "bg-red-dark/10 text-red-dark"
                    }`}
                  >
                    {monitoring.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
                <p className="text-sm text-text-secondary truncate">
                  Last check:{" "}
                  {monitoring.lastChecked
                    ? new Date(monitoring.lastChecked).toLocaleString()
                    : "Never"}
                </p>
              </div>
            </button>
          ))}
        </div>

        <div className="py-6">
          {activeTab === "settings" ? (
            <EmailMonitoringSettings
              monitoringList={monitoringList}
              onSelectMonitoring={setSelectedMonitoring}
              onAddMonitoring={() => setShowAddModal(true)}
              onDeleteClick={handleDeleteClick}
              onRefresh={onMonitoringUpdate}
            />
          ) : activeTab === "analytics" && selectedMonitoring ? (
            <DetailedAnalytics monitoring={selectedMonitoring} />
          ) : activeTab === "history" && selectedMonitoring ? (
            <EmailMonitoringHistory monitoring={selectedMonitoring} />
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-full bg-yellow-DEFAULT/5 flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-yellow-DEFAULT" />
              </div>
              <h3 className="text-lg font-medium mb-2">Select a Monitor</h3>
              <p className="text-text-secondary max-w-md">
                Choose a monitoring configuration from above to view{" "}
                {activeTab.toLowerCase()}.
              </p>
            </div>
          )}
        </div>
      </div>

      <AddMonitoringModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleCreateMonitoring}
      />

      <DeleteMonitoringModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setMonitoringToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        monitoring={monitoringToDelete}
      />
    </div>
  );
};

export default EmailMonitoringDashboard;
